import {
  constrainViewport,
  fitToScreen,
  screenToDocument,
  viewportFromGesture,
  zoomViewportAt,
} from "./canvasMath";
import type { Dimensions, Point, ViewportTransform } from "./canvasTypes";

export class ViewportController {
  private viewportSize: Dimensions = { width: 0, height: 0 };
  private transform: ViewportTransform = { zoom: 1, panX: 0, panY: 0 };

  public constructor(private readonly documentSize: Dimensions) {}

  public getTransform(): ViewportTransform {
    return { ...this.transform };
  }

  public getViewportSize(): Dimensions {
    return { ...this.viewportSize };
  }

  public resize(width: number, height: number): ViewportTransform {
    const nextSize = {
      width: Math.max(1, width),
      height: Math.max(1, height),
    };

    if (this.viewportSize.width === 0 || this.viewportSize.height === 0) {
      this.viewportSize = nextSize;
      this.transform = fitToScreen(this.documentSize, nextSize);
      return this.getTransform();
    }

    const previousCentre = {
      x: this.viewportSize.width / 2,
      y: this.viewportSize.height / 2,
    };
    const documentCentre = screenToDocument(previousCentre, this.transform);

    this.viewportSize = nextSize;
    this.transform = constrainViewport(
      {
        zoom: this.transform.zoom,
        panX: nextSize.width / 2 - documentCentre.x * this.transform.zoom,
        panY: nextSize.height / 2 - documentCentre.y * this.transform.zoom,
      },
      this.documentSize,
      nextSize,
    );

    return this.getTransform();
  }

  public fit(): ViewportTransform {
    this.transform = fitToScreen(this.documentSize, this.viewportSize);
    return this.getTransform();
  }

  public reset(): ViewportTransform {
    this.transform = constrainViewport(
      {
        zoom: 1,
        panX: (this.viewportSize.width - this.documentSize.width) / 2,
        panY: (this.viewportSize.height - this.documentSize.height) / 2,
      },
      this.documentSize,
      this.viewportSize,
    );
    return this.getTransform();
  }

  public panBy(delta: Point): ViewportTransform {
    this.transform = constrainViewport(
      {
        ...this.transform,
        panX: this.transform.panX + delta.x,
        panY: this.transform.panY + delta.y,
      },
      this.documentSize,
      this.viewportSize,
    );
    return this.getTransform();
  }

  public zoomAt(anchor: Point, zoom: number): ViewportTransform {
    this.transform = constrainViewport(
      zoomViewportAt(this.transform, zoom, anchor),
      this.documentSize,
      this.viewportSize,
    );
    return this.getTransform();
  }

  public applyGesture(
    start: ViewportTransform,
    startCentre: Point,
    currentCentre: Point,
    scaleRatio: number,
  ): ViewportTransform {
    this.transform = constrainViewport(
      viewportFromGesture(start, startCentre, currentCentre, scaleRatio),
      this.documentSize,
      this.viewportSize,
    );
    return this.getTransform();
  }
}
